import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Users, UserPlus, Star } from "lucide-react";
import { CustomerTable } from "@/components/customers/CustomerTable";
import { CustomerQuickView } from "@/components/customers/CustomerQuickView";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const { can } = usePermissions();
  const [quickViewId, setQuickViewId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    contacts: [],
    sms_opt_in: false,
    invoice_contact_delivery: "both",
    tags: []
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
        toast.success("Customer updated successfully");
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success("Customer created successfully");
      }
      setOpen(false);
      setFormData({ name: "", email: "", phone: "", address: "", contacts: [], sms_opt_in: false, tags: [] });
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(editingCustomer ? "Failed to update customer" : "Failed to create customer");
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      contacts: customer.contacts || [],
      sms_opt_in: !!customer.sms_opt_in,
      invoice_contact_delivery: customer.invoice_contact_delivery || "both",
      tags: customer.tags || []
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success("Customer deleted successfully");
      fetchCustomers();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...(formData.contacts || []), { name: "", email: "", phone: "", title: "", is_primary: false, sms_opt_in: false }]
    });
  };

  const removeContact = (index) => {
    const newContacts = (formData.contacts || []).filter((_, i) => i !== index);
    setFormData({ ...formData, contacts: newContacts });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...(formData.contacts || [])];
    if (field === 'is_primary' && value) {
      // Unset other primary contacts
      newContacts.forEach((c, i) => {
        if (i !== index) c.is_primary = false;
      });
    }
    newContacts[index][field] = value;
    setFormData({ ...formData, contacts: newContacts });
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", address: "", contacts: [] });
    setEditingCustomer(null);
  };

  return (
    <div data-testid="customers-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Customers</h1>
          <p className="text-slate-600">Manage your customer database</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className={`bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg ${can("customers.create") ? "" : "hidden"}`} data-testid="add-customer-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? "Edit Customer" : "Add New Customer"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="customer-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="customer-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="address">Address *</Label>
                <Textarea
                  id="address"
                  data-testid="customer-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  className="mt-1"
                  rows={3}
                />
              </div>
              <label className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm cursor-pointer" data-testid="customer-sms-consent-box">
                <input type="checkbox" checked={!!formData.sms_opt_in} onChange={(e) => setFormData({ ...formData, sms_opt_in: e.target.checked })} className="mt-0.5 accent-blue-700" data-testid="customer-sms-consent-checkbox" />
                <span><span className="font-medium text-slate-800">SMS consent</span><span className="block text-xs text-slate-500">Customer agreed to receive marketing texts (campaigns). Invoice and reminder texts only require a mobile number.</span></span>
              </label>
              <div>
                <Label htmlFor="tags">Tags <span className="text-xs text-slate-500">(comma separated, used for campaign audiences)</span></Label>
                <Input id="tags" value={(formData.tags || []).join(", ")} onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} placeholder="residential, vip" className="mt-1" data-testid="customer-tags-input" />
              </div>
              
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-3">
                  <Label className="text-base font-semibold">Contact Persons</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addContact}>
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Contact
                  </Button>
                </div>
                
                {(formData.contacts || []).map((contact, index) => (
                  <div key={index} className="border rounded-lg p-3 mb-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">Contact {index + 1}</span>
                        {contact.is_primary && (
                          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
                            <Star className="w-3 h-3 mr-1" />
                            Primary
                          </Badge>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeContact(index)}
                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Name"
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        className="text-sm"
                      />
                      <Input
                        placeholder="Title"
                        value={contact.title}
                        onChange={(e) => updateContact(index, 'title', e.target.value)}
                        className="text-sm"
                      />
                      <Input
                        type="email"
                        placeholder="Email"
                        value={contact.email}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        className="text-sm"
                      />
                      <Input
                        placeholder="Phone"
                        value={contact.phone}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={contact.is_primary}
                          onChange={(e) => updateContact(index, 'is_primary', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-gray-700">Set as primary contact</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`contact-sms-opt-in-${index}`}>
                        <input
                          type="checkbox"
                          checked={!!contact.sms_opt_in}
                          onChange={(e) => updateContact(index, 'sms_opt_in', e.target.checked)}
                          className="rounded"
                          data-testid={`contact-sms-opt-in-checkbox-${index}`}
                        />
                        <span className="text-gray-700">OK to text this phone (invoices)</span>
                      </label>
                    </div>
                  </div>
                ))}
                
                {(!formData.contacts || formData.contacts.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No contact persons added</p>
                )}
                <div className="mt-3">
                  <Label htmlFor="invoice_contact_delivery">Send invoices to primary contact</Label>
                  <Select value={formData.invoice_contact_delivery || "both"} onValueChange={(v) => setFormData({ ...formData, invoice_contact_delivery: v })}>
                    <SelectTrigger id="invoice_contact_delivery" className="mt-1" data-testid="invoice-contact-delivery-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both (email + text)</SelectItem>
                      <SelectItem value="email">Email only</SelectItem>
                      <SelectItem value="sms">Text only</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Invoices, reminders and overdue notices also go to the primary contact when their email/phone differs from the customer's. Texts only go to phones marked "OK to text".</p>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-customer-button">
                  {editingCustomer ? "Update" : "Create"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : customers.length === 0 ? (
        <Card className="bg-white border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No customers yet</h3>
            <p className="text-slate-500">Get started by adding your first customer</p>
          </CardContent>
        </Card>
      ) : (
        <CustomerTable
          customers={customers}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canDelete={can("customers.delete")}
          onSelect={(customer) => setQuickViewId(customer.id)}
        />
      )}
      {quickViewId && (
        <CustomerQuickView customerId={quickViewId} onClose={() => setQuickViewId(null)} onEdit={handleEdit} canAct={can("invoices.send") || can("customers.send_statement")} />
      )}
    </div>
  );
}
